import { lookupCard } from "../../content/index.js";
import { applyEvent } from "../resolution/applyEvent.js";
import { planEvents } from "../resolution/planEvents.js";
import { beginPhase } from "../run/beginPhase.js";
import type { GameState, ResolutionEvent } from "../types/index.js";
import { appendLog, appendReshuffleLogIfNeeded } from "../run/log.js";
import {
  setupFailure,
  setupGameover,
  setupSuccessRewards,
} from "./chooseReward.js";
import { accept, reject } from "./reject.js";

function isTerminal(event: ResolutionEvent): boolean {
  return event.kind === "success" || event.kind === "failure" || event.kind === "gameover";
}

function applyCurrentEvent(state: GameState): GameState {
  if (!state.run?.activeCase || !state.resolution) return state;

  const event = state.resolution.events[state.resolution.index];
  if (!event) return state;

  const applied = applyEvent(state.run, state.run.activeCase, event);
  let run = applied.run;

  const kindMap = {
    progress: "progress",
    heal: "system",
    load: "load",
    deadline: "system",
    discard: "system",
    overdue: "system",
    success: "system",
    failure: "system",
    gameover: "system",
  } as const;

  if (event.message) {
    run = appendLog(run, {
      phase: applied.activeCase.phase,
      kind: kindMap[event.kind],
      message: event.message,
    });
  }

  return { ...state, run: { ...run, activeCase: applied.activeCase } };
}

function beginNextPhase(state: GameState): GameState {
  const activeCase = state.run?.activeCase;
  if (!state.run || !activeCase) return state;

  let run = state.run;
  const targetHand = 5;
  const willReshuffle = run.discard.length > 0 && run.deck.length < targetHand;
  run = beginPhase({ run, activeCase, extraDraw: 0 });
  run = appendReshuffleLogIfNeeded(state.run, run, activeCase.phase + 1, willReshuffle);

  return accept({
    ...state,
    screen: "case.play",
    run,
    resolution: null,
  });
}

export function startResolution(state: GameState): GameState {
  if (!state.run?.activeCase) return state;

  const planned = planEvents({ run: state.run, activeCase: state.run.activeCase });
  // 労働者の進捗は計画時に確定させてから、解決中の実行済みレーンへ移す。
  // 次フェーズ開始時の discardPlayed で施策と一緒に捨て札へ移り、表示から消える。
  const workers = state.run.hand.filter((card) => lookupCard(card.cardId)?.type === "worker");
  const hand = state.run.hand.filter((card) => lookupCard(card.cardId)?.type !== "worker");
  const run = { ...state.run, hand, inPlay: [...state.run.inPlay, ...workers], rngState: planned.rngState };
  const next = applyCurrentEvent({
    ...state,
    run,
    screen: "case.resolve",
    resolution: { events: planned.events, index: 0 },
  });

  return accept(next);
}

export function handleEndPhase(state: GameState): GameState {
  if (state.screen !== "case.play" || !state.run?.activeCase) {
    return reject(state, "プロジェクト進行中のみフェーズを終了できる");
  }
  if (state.run.pendingChoice) {
    return reject(state, "選択待ち中はフェーズを終了できない");
  }

  return startResolution(state);
}

export function handleAdvanceResolution(state: GameState): GameState {
  if (state.screen !== "case.resolve" || !state.resolution || !state.run?.activeCase) {
    return reject(state, "解決中のみ進められる");
  }

  const current = state.resolution.events[state.resolution.index];
  if (!current) return reject(state, "解決イベントがない");

  if (isTerminal(current)) {
    if (current.kind === "success") return setupSuccessRewards(state);
    if (current.kind === "failure") return setupFailure(state);
    return setupGameover(state);
  }

  const nextIndex = state.resolution.index + 1;
  const nextEvent = state.resolution.events[nextIndex];

  if (!nextEvent) {
    return beginNextPhase(state);
  }

  const advanced = applyCurrentEvent({
    ...state,
    resolution: { ...state.resolution, index: nextIndex },
  });

  if (isTerminal(nextEvent)) {
    return accept(advanced);
  }

  return accept(advanced);
}
